import { createContext, useContext } from "react";

import {
  AssetInventoryDocumentsSliceApi,
  AssetInventorySliceApi,
  BankruptcyChecklistSliceApi,
  ClaimsSliceApi,
  ClientFileApi,
  ClientNoteSliceApi,
  DocumentDndSliceApi,
  LookupsSliceApi,
  ParticipantsSliceApi,
} from "./ClientFileApi";

// Shared client-file code reaches the backend only through this context: each app mounts the
// provider once with an adapter built from its own generated api-client (shared code cannot import
// one — see ../../auth/authMiddleware.ts). Slices are optional so the portal can enable areas one
// by one as the backend exposes them; area UIs must not render when their slice is absent, so the
// accessors below throw on a missing slice instead of failing later on an undefined call.
export const ClientFileApiContext = createContext<ClientFileApi>({});

export function useClientFileApi(): ClientFileApi {
  return useContext(ClientFileApiContext);
}

export function useBankruptcyChecklistApi(): BankruptcyChecklistSliceApi {
  const { bankruptcyChecklist } = useContext(ClientFileApiContext);
  if (!bankruptcyChecklist) {
    throw new Error("ClientFileApi.bankruptcyChecklist is not provided — mount ClientFileApiProvider with this slice.");
  }
  return bankruptcyChecklist;
}

export function useAssetInventoryApi(): AssetInventorySliceApi {
  const assetInventory = useOptionalAssetInventoryApi();
  if (!assetInventory) {
    throw new Error("ClientFileApi.assetInventory is not provided — mount ClientFileApiProvider with this slice.");
  }
  return assetInventory;
}

/** For indicators outside the asset-inventory area (navigation tabs), which render whether or not the slice exists. */
export function useOptionalAssetInventoryApi(): AssetInventorySliceApi | undefined {
  return useContext(ClientFileApiContext).assetInventory;
}

/**
 * The asset-inventory document slice is optional even where the asset-inventory forms render (the
 * portal enables the documents in a later sub-phase), so this accessor returns undefined instead of
 * throwing — the upload blocks presence-gate themselves on it.
 */
export function useOptionalAssetInventoryDocumentsApi(): AssetInventoryDocumentsSliceApi | undefined {
  const { assetInventoryDocuments } = useContext(ClientFileApiContext);
  return assetInventoryDocuments;
}

/** Optional: only the internal app provides drag-and-drop; the document blocks fall back when absent. */
export function useOptionalDocumentDndApi(): DocumentDndSliceApi | undefined {
  const { documentDnd } = useContext(ClientFileApiContext);
  return documentDnd;
}

export function useLookupsApi(): LookupsSliceApi {
  const { lookups } = useContext(ClientFileApiContext);
  if (!lookups) {
    throw new Error("ClientFileApi.lookups is not provided — mount ClientFileApiProvider with this slice.");
  }
  return lookups;
}

export function useClaimsApi(): ClaimsSliceApi {
  const { claims } = useContext(ClientFileApiContext);
  if (!claims) {
    throw new Error("ClientFileApi.claims is not provided — mount ClientFileApiProvider with this slice.");
  }
  return claims;
}

export function useParticipantsApi(): ParticipantsSliceApi {
  const { participants } = useContext(ClientFileApiContext);
  if (!participants) {
    throw new Error("ClientFileApi.participants is not provided — mount ClientFileApiProvider with this slice.");
  }
  return participants;
}

export function useClientNoteApi(): ClientNoteSliceApi {
  const { clientNote } = useContext(ClientFileApiContext);
  if (!clientNote) {
    throw new Error("ClientFileApi.clientNote is not provided — mount ClientFileApiProvider with this slice.");
  }
  return clientNote;
}
